import type { Locale } from "@/i18n/config";

const localeTags: Record<Locale, string> = {
  ko: "ko-KR",
  en: "en-US",
  ja: "ja-JP",
  zh: "zh-CN",
  "zh-tw": "zh-TW",
};

export function formatPrice(amountKRW: number, locale: Locale): string {
  return new Intl.NumberFormat(localeTags[locale], {
    style: "currency",
    currency: "KRW",
    maximumFractionDigits: 0,
  }).format(amountKRW);
}

export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(localeTags[locale]).format(value);
}

/** Formats a Seoul-local date ISO (YYYY-MM-DD) in the given locale */
export function formatDateISO(
  dateISO: string,
  locale: Locale,
  options?: Intl.DateTimeFormatOptions,
): string {
  const [y, m, d] = dateISO.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return new Intl.DateTimeFormat(localeTags[locale], {
    timeZone: "UTC",
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "short",
    ...options,
  }).format(date);
}

/** Formats a UTC ISO timestamp as Seoul-local date & time */
export function formatDateTimeSeoul(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(localeTags[locale], {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(iso));
}

export function formatTimeRange(startMinute: number, durationMinutes: number) {
  const end = startMinute + durationMinutes;
  const fmt = (m: number) =>
    `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  return `${fmt(startMinute)} – ${fmt(end)}`;
}

export type PaypalCurrency = "CURRENCY_USD" | "CURRENCY_EUR" | "CURRENCY_JPY";

export interface PaypalCurrencyConfig {
  code: PaypalCurrency;
  isoCode: "USD" | "EUR" | "JPY";
  symbol: string;
  label: string;
  rate: number; // KRW per 1 foreign currency
  minorDecimals: number;
}

export const PAYPAL_CURRENCIES: Record<PaypalCurrency, PaypalCurrencyConfig> = {
  CURRENCY_USD: {
    code: "CURRENCY_USD",
    isoCode: "USD",
    symbol: "$",
    label: "USD ($)",
    rate: 1400,
    minorDecimals: 2,
  },
  CURRENCY_EUR: {
    code: "CURRENCY_EUR",
    isoCode: "EUR",
    symbol: "€",
    label: "EUR (€)",
    rate: 1500,
    minorDecimals: 2,
  },
  CURRENCY_JPY: {
    code: "CURRENCY_JPY",
    isoCode: "JPY",
    symbol: "¥",
    label: "JPY (¥)",
    rate: 9.5,
    minorDecimals: 0,
  },
};

export function convertToPaypalCurrency(
  amountKRW: number,
  currency: PaypalCurrency,
): { mainAmount: number; minorAmount: number; formatted: string } {
  const config = PAYPAL_CURRENCIES[currency] || PAYPAL_CURRENCIES.CURRENCY_USD;
  const foreignValue = amountKRW / config.rate;
  if (config.minorDecimals === 0) {
    const mainAmount = Math.max(1, Math.round(foreignValue));
    const formatted = `${config.symbol}${mainAmount.toLocaleString()}`;
    return { mainAmount, minorAmount: mainAmount, formatted };
  } else {
    const mainAmount = Math.max(0.01, Math.round(foreignValue * 100) / 100);
    const minorAmount = Math.round(mainAmount * 100);
    const formatted = `${config.symbol}${mainAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return { mainAmount, minorAmount, formatted };
  }
}

